// Drag-paint stroke model, shared by the 3D and 2.5D input paths so both behave
// identically. A paintable order (road / dig / fill) is laid by press-and-drag: the
// hexes the cursor crosses accumulate into st.paintPath as a live HIGHLIGHT only —
// nothing reaches the server until the button is released (paintCommit). Dragging
// back over an earlier hex truncates the path to it, so retracing un-highlights the
// tail. Pure view/UI state: this never mutates the world, only st.paintPath/paintType.
//
// The stroke captures its build type at paintBegin (st.paintType) and uses that for
// both the highlight and the commit — so a stray tool change mid-drag can't retype the
// stroke. Each hex caches its ghostValid verdict at add-time (`ok`) so the renderer
// colours the whole path without recomputing validity for every hex every frame.

import type { GameState } from './state'
import type { BuildingType } from '../shared/types'
import type { ClientMsg } from '../shared/protocol'
import { ghostValid } from './render/renderer'

/** Begin a stroke of `type` at (q,r) (replaces any stale path). */
export function paintBegin(st: GameState, type: BuildingType, q: number, r: number): void {
  st.paintType = type
  st.paintPath = [{ q, r, ok: ghostValid(st, type, q, r) }]
}

/** Extend the in-progress stroke to cover hex (q,r), highlight-only.
 *  - already the head → no-op (cursor still on the same tile)
 *  - already earlier in the path → truncate to it (back-drag erases the tail)
 *  - otherwise → append it (with its cached validity). */
export function paintExtend(st: GameState, q: number, r: number): void {
  const type = st.paintType
  if (!type) return
  const path = st.paintPath
  const n = path.length
  if (n > 0 && path[n - 1].q === q && path[n - 1].r === r) return
  for (let i = 0; i < n; i++) {
    if (path[i].q === q && path[i].r === r) { path.length = i + 1; return }
  }
  path.push({ q, r, ok: ghostValid(st, type, q, r) })
}

/** Release the stroke: send a build for every hex that STILL passes the placement rule
 *  (re-checked fresh, in case the world shifted since it was drawn), in draw order,
 *  then clear the highlight. Returns how many orders were sent. Paintable orders never
 *  carry a facing, so a plain build message is exact. */
export function paintCommit(st: GameState, send: (m: ClientMsg) => void): number {
  const type = st.paintType
  let sent = 0
  if (type) {
    for (const c of st.paintPath) {
      if (ghostValid(st, type, c.q, c.r)) { send({ t: 'build', type, q: c.q, r: c.r }); sent++ }
    }
  }
  st.paintPath = []
  st.paintType = null
  return sent
}

/** Abandon the stroke, discarding the highlight (Esc, pointer-cancel, blur, disarm). */
export function paintCancel(st: GameState): void {
  if (st.paintPath.length) st.paintPath = []
  st.paintType = null
}
